'use client'

import { DEFAULT_PRICING_RULES, type PricingRuleValues } from '@repo/db/pricing-rules'
import { createContext, type ReactNode, useContext } from 'react'

/**
 * The rules the basket has to quote, read once on the server.
 *
 * A context and not a prop, for the reason `ToastProvider` is one: the only
 * consumer that needs the footage rate is `FootageField`, three hops down
 * through `OrderLineCard` and `OrderLineFields` — neither of which has any
 * business knowing what a minute of tape costs. Threading it would put a
 * pricing prop on two components that only lay things out.
 *
 * Read-only and set once per render. Nothing writes through it: the panel's
 * save goes to the server and the page is revalidated, which is the only way a
 * figure here is allowed to change.
 *
 * The default is what the site charged before any of this was editable, so a
 * component rendered outside the provider — a story, a unit test — prices
 * exactly as it always did rather than at zero.
 */
const PricingRulesContext = createContext<PricingRuleValues>(DEFAULT_PRICING_RULES)

export function PricingRulesProvider({
  rules,
  children,
}: {
  rules: PricingRuleValues
  children: ReactNode
}) {
  return <PricingRulesContext.Provider value={rules}>{children}</PricingRulesContext.Provider>
}

export function usePricingRules(): PricingRuleValues {
  return useContext(PricingRulesContext)
}
